import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { Modal } from 'reactstrap';
import PropTypes from 'prop-types';
import moment from 'moment';
import GroupEditModalForm from './GroupEditModalForm';
import config from '../../../../config';
import Notify from '../../../../shared/components/widgets/Notify';

const GroupEditModal = ({ currentEditRow, changeShowEditModal, showEditModal, setForceRefresh, budgetAccountData, activeDayOptions, disableButton, setDisableButton, setShowEditModal, retailerConfig }) => {
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);

  const monthOptions = [
    { label: 'January', name: 'january', value: '1' },
    { label: 'February', name: 'february', value: '2' },
    { label: 'March', name: 'march', value: '3' },
    { label: 'April', name: 'april', value: '4' },
    { label: 'May', name: 'may', value: '5' },
    { label: 'June', name: 'june', value: '6' },
    { label: 'July', name: 'july', value: '7' },
    { label: 'August', name: 'august', value: '8' },
    { label: 'September', name: 'september', value: '9' },
    { label: 'October', name: 'october', value: '10' },
    { label: 'November', name: 'november', value: '11' },
    { label: 'December', name: 'december', value: '12' },
  ];

  const handleSubmit = (formData) => {
    setDisableButton(true);
    const activeDaysArray = [];

    activeDayOptions.forEach(day => {
      activeDaysArray.push(formData[day.name] ? '1' : '0');
    });

    // Get budget allocation percentages
    const allocationSpSd = parseFloat(formData.budgetAllocationSpSd) || 0;
    const allocationSb = parseFloat(formData.budgetAllocationSb) || 0;
    const allocationSt = parseFloat(formData.budgetAllocationSt) || 0;

    // Check if we need to split items into individual budget groups
    if (formData.splitItemsCheckbox && formData.masterItemOnList) {
      // Parse the masterItemOnList to get individual items
      const itemList = formData.masterItemOnList
        .toString()
        .trim()
        .replace(/\r/g, '')
        .replace(/\s*,\s*/g, ',')
        .replace(/\n+/g, ',')
        .split(',')
        .filter(item => item.trim() !== '');

      if (itemList.length === 0) {
        setDisableButton(false);
        Notify.show('danger', 'Error', 'No items found in the master item list');
        return;
      }

      // Build original monthly values for split items
      const originalMonthly = {};
      monthOptions.forEach(month => {
        originalMonthly[month.name] = parseFloat(formData[month.name]) || 0;
      });

      // Create budget_json for split items (no allocation split)
      const itemBudgetJson = {
        allocation: {
          percentages: {
            sp_sd_percent: 100,
            sb_percent: 0,
            st_percent: 0,
          },
          original: {
            daily: {
              budget: parseFloat(formData.baseDailyBudgetAmount) || 0,
              reallocation: parseFloat(formData.reallocation) || 0,
            },
            monthly: originalMonthly,
          },
        },
      };

      // Create a budget group for each item
      const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      let successCount = 0;
      let errorCount = 0;
      const promises = itemList.map((item) => {
        const itemBudgetGroupName = `${formData.budgetGroupName}_${item.trim()}`;

        // For split items, determine which ad types are active based on form selection
        // Default is 100% SP,SD (0% SB, 0% ST)
        const splitAdTypes = formData.adTypeCsv?.value || 'SP,SD';

        // Count how many ad types are in the selection to know how many 0s to send
        const adTypeCount = splitAdTypes.split(',').filter(at => at.trim()).length;
        const splitIds = Array(adTypeCount).fill(0).join(','); // e.g., "0" or "0,0" or "0,0,0"

        const submitBudgetGroupDaily = {
          accountId: sellerSelect.value,
          accountTypeId: sellerSelect.type,
          budgetGroupDailyIdCsv: splitIds, // CSV of 0s for new budget groups
          budgetGroupName: itemBudgetGroupName,
          setupFlow: formData.setupFlow?.value,
          retailer: retailerConfig.key,
          agencyInternal: formData.agencyInternal?.value,
          activeDaysCsv: activeDaysArray.join(','),
          adTypeCsv: splitAdTypes,
          portfolioIdsCsv: formData.portfolioIdsCsv,
          includes: formData.includes.value,
          costTargetType: formData.costTargetType.value,
          costTargetMetric: formData.costTargetMetric,
          costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
          costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
          spendSpeed: formData.spendSpeed.value,
          spendTactic: formData.spendTactic.value,
          budgetType: formData.budgetType.value,
          spendPriority: formData.spendPriority.value,
          itemType: formData.itemType?.value,
          itemSelection: formData.itemSelection?.value,
          destination: formData.destination?.value,
          masterItemOrigList: item.trim(),
          masterItemOnList: item.trim(),
          masterItemOffList: '',
          startDate: formData.startDate,
          endDate: formData.endDate,
          budgetDriver: formData.budgetDriver.value,
          budgetJson: itemBudgetJson,
          state: formData.state.value,
        };

        const requestOptions = {
          method: 'POST',
          headers: headerWithAuth,
          body: JSON.stringify(submitBudgetGroupDaily),
        };

        return fetch(postUrl, requestOptions)
          .then((results) => {
            if (!results.ok) {
              throw Error(results.statusText);
            }
            successCount += 1;
            return results.json();
          })
          .catch((error) => {
            errorCount += 1;
            console.error(`Error creating budget group for ${item}:`, error);
            return null;
          });
      });

      Promise.all(promises)
        .then(() => {
          changeShowEditModal();
          setDisableButton(false);
          setForceRefresh(moment().utc());

          if (successCount > 0) {
            Notify.show('success', 'Success', `Successfully created ${successCount} budget group(s)`);
          }
          if (errorCount > 0) {
            Notify.show('warning', 'Warning', `Failed to create ${errorCount} budget group(s)`);
          }
        })
        .catch((error) => {
          Notify.show('danger', 'Error', `An error occurred while creating budget groups: ${error}`);
          console.error(error);
          setDisableButton(false);
        });
      return;
    }

    // Make single API call - backend will handle the structured budgetGroupRecords
    if (formData.budgetGroupName) {
      const postUrl = `${config.serverUrl}/accounts/budget-group-daily`;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };

      // Build budgetGroupRecords with allocation percentages
      const budgetGroupRecords = formData.budgetGroupRecords || [
        { budget_group_daily_id: 0, ad_type: 'SP,SD', allocation_percent: allocationSpSd },
        { budget_group_daily_id: 0, ad_type: 'SB', allocation_percent: allocationSb },
        { budget_group_daily_id: 0, ad_type: 'ST', allocation_percent: allocationSt },
      ];

      // Build original monthly values
      const originalMonthly = {};
      monthOptions.forEach(month => {
        originalMonthly[month.name] = parseFloat(formData[month.name]) || 0;
      });

      // Create budget_json with only allocation and original values
      // Backend will handle daily and monthly node calculations
      const budgetJson = {
        allocation: {
          percentages: {
            sp_sd_percent: allocationSpSd,
            sb_percent: allocationSb,
            st_percent: allocationSt,
          },
          original: {
            daily: {
              budget: parseFloat(formData.baseDailyBudgetAmount) || 0,
              reallocation: parseFloat(formData.reallocation) || 0,
            },
            monthly: originalMonthly,
          },
        },
      };

      const submitBudgetGroupDaily = {
        accountId: sellerSelect.value,
        accountTypeId: sellerSelect.type,
        budgetGroupRecords, // Send structured records array instead of CSV
        budgetGroupName: formData.budgetGroupName,
        setupFlow: formData.setupFlow?.value,
        retailer: retailerConfig.key,
        agencyInternal: formData.agencyInternal?.value,
        activeDaysCsv: activeDaysArray.join(','),
        portfolioIdsCsv: formData.portfolioIdsCsv,
        includes: formData.includes.value,
        costTargetType: formData.costTargetType.value,
        costTargetMetric: formData.costTargetMetric,
        costTargetBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetBrand : formData.costTarget,
        costTargetNonBrand: formData.costTargetType.value === 'bnb' ? formData.costTargetNonBrand : formData.costTarget,
        spendSpeed: formData.spendSpeed.value,
        spendTactic: formData.spendTactic.value,
        budgetType: formData.budgetType.value,
        spendPriority: formData.spendPriority.value,
        itemType: formData.itemType?.value,
        itemSelection: formData.itemSelection?.value,
        destination: formData.destination?.value,
        masterItemOrigList: formData.masterItemOrigList || formData.masterItemOnList || '',
        masterItemOnList: formData.masterItemOnList || '',
        masterItemOffList: formData.masterItemOffList || '',
        startDate: formData.startDate,
        endDate: formData.endDate,
        budgetDriver: formData.budgetDriver.value,
        budgetJson, // Only allocation data - backend calculates daily/monthly
        state: formData.state.value,
      };

      const requestOptions = {
        method: 'POST',
        headers: headerWithAuth,
        body: JSON.stringify(submitBudgetGroupDaily),
      };

      // Single API call
      fetch(postUrl, requestOptions)
        .then((results) => {
          if (!results.ok) {
            throw Error(results.statusText);
          }
          return results.json();
        })
        .then(() => {
          changeShowEditModal();
          setDisableButton(false);
          setForceRefresh(moment().utc());
          Notify.show('success', 'Success', 'Budget group saved successfully');
        })
        .catch((error) => {
          Notify.show('danger', 'Error', `We were unable to update your budget. Error: ${error}`);
          console.error(error);
          setDisableButton(false);
        });
    }
  };

  const modalClasses = classNames({
    'todo__add-modal': true,
  }, theme.className, 'ltr-support');

  return (
    <Modal
      isOpen={showEditModal}
      toggle={changeShowEditModal}
      className={modalClasses}
    >
      <GroupEditModalForm
        onSubmit={handleSubmit}
        initialValues={currentEditRow}
        // handleSubmit={handleSubmit} // Try to figure this out.  onSubmit and initalValues seem to be defualt props for redux?
        currentEditRow={currentEditRow}
        changeShowEditModal={changeShowEditModal}
        activeDayOptions={activeDayOptions}
        monthOptions={monthOptions}
        user={user}
        budgetAccountData={budgetAccountData}
        disableButton={disableButton}
        setDisableButton={setDisableButton}
        setShowEditModal={setShowEditModal}
        retailerConfig={retailerConfig}
      />
    </Modal>
  );
};

GroupEditModal.propTypes = {
  currentEditRow: PropTypes.shape(),
  changeShowEditModal: PropTypes.func.isRequired,
  showEditModal: PropTypes.bool.isRequired,
  setForceRefresh: PropTypes.func.isRequired,
};

GroupEditModal.defaultProps = {
  currentEditRow: null,
};

export default GroupEditModal;
